// Cookie consent state model + persistence.
//
// The owner chose Option A (two equal buttons: accept-all vs reject-all). We
// still split the boolean into three categories internally — that way when GA
// or Meta Pixel is wired up later, only the right category enables the right
// script. The banner UI doesn't surface the categories; "Nastavenia cookies"
// in the footer reopens the banner so a future iteration could add per-
// category toggles without a schema change.
//
// Slovak Act 452/2021 Z. z. § 109 + GDPR consent recital 32: consent must be
// freely given, specific, informed and unambiguous. We satisfy this by
// requiring an explicit click on either accept-all or reject-all; the banner
// blocks below all content (z-50) but does NOT block interaction with the
// rest of the page, so legitimate-interest essential cookies can still be set
// and the SPA stays usable.
//
// Privacy-policy lifecycle: bump CONSENT_VERSION when categories change so
// stored consents are re-asked. Stored version mismatch is treated as "no
// consent stored".

export const CONSENT_VERSION = 1 as const;
const STORAGE_KEY = 'dr_consent' as const;

export type ConsentCategories = {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentRecord = {
  version: number;
  timestamp: number; // unix seconds
  categories: ConsentCategories;
};

export const DENY_ALL: ConsentCategories = {
  functional: false,
  analytics: false,
  marketing: false,
};

export const ALLOW_ALL: ConsentCategories = {
  functional: true,
  analytics: true,
  marketing: true,
};

// Read the stored consent, or null if no decision has been made (or if the
// version has moved on since the user last decided).
export function readStoredConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (
      typeof parsed.version !== 'number' ||
      typeof parsed.timestamp !== 'number' ||
      !parsed.categories ||
      typeof parsed.categories.functional !== 'boolean' ||
      typeof parsed.categories.analytics !== 'boolean' ||
      typeof parsed.categories.marketing !== 'boolean'
    ) {
      return null;
    }
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed as ConsentRecord;
  } catch {
    return null;
  }
}

export function writeStoredConsent(categories: ConsentCategories): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    timestamp: Math.floor(Date.now() / 1000),
    categories,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Ignore quota / disabled-storage; in-memory state in useConsent still
    // works for the session. The banner will re-prompt next page load.
  }
  return record;
}

export function clearStoredConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Global Privacy Control (RFC editor draft, supported in Firefox + Brave).
// When set, treated as a legally valid signal of "no consent for non-essential
// cookies" per CNIL / EDPB guidance.
export function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true;
}
